// Physics Utilities
// Shared utility functions for physics calculations

import { PhysicsEntity, Platform } from '../../../shared/CharacterTypes';
import { Bounds } from './types/CollisionTypes';
import { gameLogger } from '../../../utils/GameLogger';
import { PLAYER_HITBOX } from '../../../shared/logic/CombatAnchors';

export class PhysicsUtils {
  /**
     * Check if a position is valid (not NaN)
     */
  public static isValidPosition(pos: { x: number, y: number } | undefined | null): boolean {
    return !!pos && !isNaN(pos.x) && !isNaN(pos.y);
  }

  /**
     * Get character bounds for collision detection
     */
  public static getCharacterBounds(character: PhysicsEntity): Bounds {
    // CRITICAL: Ensure character.position exists and is valid
    if (!this.isValidPosition(character.position)) {
      gameLogger.error('physics', '❌ Invalid character position detected by PhysicsUtils:', character.position);
      // Return safe bounds to prevent crash
      return {
        left: 0,
        right: PLAYER_HITBOX.width,
        top: 0,
        bottom: PLAYER_HITBOX.height
      };
    }

    // Same fallback the combat anchor uses, from the same constant — a body
    // whose bounds and whose combat centre disagreed about its size would put
    // the cone origin off the body it collides with.
    const width = character.width && character.width > 0 ? character.width : PLAYER_HITBOX.width;
    const height = character.height && character.height > 0 ? character.height : PLAYER_HITBOX.height;

    return {
      left: character.position.x,
      right: character.position.x + width,
      top: character.position.y,
      bottom: character.position.y + height
    };
  }

  /**
     * Get platform bounds for collision detection
     */
  public static getPlatformBounds(platform: Platform): Bounds {
    return {
      left: platform.x,
      right: platform.x + platform.width,
      top: platform.y,
      bottom: platform.y + platform.height
    };
  }

  /**
     * Check if two bounds are colliding
     */
  public static isColliding(bounds1: Bounds, bounds2: Bounds): boolean {
    return bounds1.left < bounds2.right &&
            bounds1.right > bounds2.left &&
            bounds1.top < bounds2.bottom &&
            bounds1.bottom > bounds2.top;
  }

  /**
     * Validate platform coordinates
     */
  public static validatePlatformCoordinates(platform: Platform): boolean {
    // Coordinates are authored in world pixels, and production maps are not
    // capped to the legacy 10,000px prototype scale. World containment belongs
    // to the loaded level bounds; this guard only rejects malformed geometry.
    const valid = Number.isFinite(platform.x) && Number.isFinite(platform.y) &&
            Number.isFinite(platform.width) && Number.isFinite(platform.height) &&
            platform.width > 0 && platform.height > 0;

    if (!valid) {
      gameLogger.error('physics', '❌ Invalid platform coordinates:', platform);
    }

    return valid;
  }

  /**
     * Calculate overlap between two bounds
     * Returns positive overlap values when colliding, negative when not colliding
     */
  public static calculateOverlap(bounds1: Bounds, bounds2: Bounds): { x: number; y: number } {
    const overlapX = Math.min(bounds1.right - bounds2.left, bounds2.right - bounds1.left);
    const overlapY = Math.min(bounds1.bottom - bounds2.top, bounds2.bottom - bounds1.top);

    return { x: overlapX, y: overlapY };
  }

  /**
     * Clamp velocity to maximum speeds
     */
  public static clampVelocity(character: PhysicsEntity): void {
    const terminalVel = character.movementStats?.terminalVelocity || 1000;

    // Horizontal clamping — a movement-ability travel window may exceed it
    // by its own speed (DashTravelState sets travelSpeedCap while moving).
    const travelCap = (character as { travelSpeedCap?: number }).travelSpeedCap ?? 0;
    const horizontalCap = Math.max(terminalVel, travelCap);
    character.velocity.x = Math.max(-horizontalCap,
      Math.min(horizontalCap, character.velocity.x));

    // Vertical clamping
    if (character.isGrounded && character.velocity.y > 0) {
      character.velocity.y = 0;
    } else {
      character.velocity.y = Math.max(-terminalVel * 1.5,
        Math.min(terminalVel, character.velocity.y));
    }
  }
}
